import type { CampaignSettings } from "@/api/email";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TimezoneSelect } from "@/components/shared/TimezoneSelect";
import { WEEKDAYS } from "@/lib/email";
import { cn } from "@/lib/utils";

export const DEFAULT_SETTINGS: CampaignSettings = {
  timezone: "Europe/Paris",
  sending_days: [1, 2, 3, 4, 5],
  sending_start_time: "09:00",
  sending_end_time: "18:00",
  daily_limit: 50,
  default_delay_days: 3,
  provider_settings: {},
};

/** What makes the settings unusable, or null. Mirrors the API's checks. */
export function settingsProblem(settings: CampaignSettings): string | null {
  if (!settings.sending_days.length) return "Pick at least one sending day.";
  if (settings.sending_start_time >= settings.sending_end_time)
    return "The sending window must end after it starts.";
  if (!(settings.daily_limit >= 1 && settings.daily_limit <= 1000))
    return "The daily limit is between 1 and 1,000.";
  if (!(settings.default_delay_days >= 0 && settings.default_delay_days <= 60))
    return "The default delay is between 0 and 60 days.";
  return null;
}

/** Sending days and hours (they become the Lemlist schedule), the daily
 *  hand-over limit and the delay given to new follow-ups. */
export function SendingSettingsFields({
  value,
  onChange,
  disabled,
}: {
  value: CampaignSettings;
  onChange: (next: CampaignSettings) => void;
  disabled?: boolean;
}) {
  const set = <K extends keyof CampaignSettings>(key: K, v: CampaignSettings[K]) =>
    onChange({ ...value, [key]: v });

  function toggleDay(day: number) {
    const days = value.sending_days.includes(day)
      ? value.sending_days.filter((d) => d !== day)
      : [...value.sending_days, day].sort((a, b) => a - b);
    set("sending_days", days);
  }

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label className="text-xs font-medium text-slate-600">Sending days</Label>
        <div className="flex flex-wrap gap-1.5">
          {WEEKDAYS.map((day) => {
            const on = value.sending_days.includes(day.value);
            return (
              <button
                key={day.value}
                type="button"
                disabled={disabled}
                aria-pressed={on}
                title={day.long}
                onClick={() => toggleDay(day.value)}
                className={cn(
                  "h-8 w-11 rounded-lg border text-xs font-semibold transition-colors",
                  on
                    ? "border-indigo-200 bg-indigo-50 text-indigo-700"
                    : "border-slate-200 bg-white text-slate-400 hover:text-slate-600",
                )}
              >
                {day.short}
              </button>
            );
          })}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-[1fr_auto_auto]">
        <div className="col-span-2 space-y-1.5 sm:col-span-1">
          <Label className="text-xs font-medium text-slate-600">Timezone</Label>
          <TimezoneSelect
            value={value.timezone}
            onValueChange={(tz) => set("timezone", tz)}
            disabled={disabled}
            className="h-9 w-full"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="sending-start" className="text-xs font-medium text-slate-600">
            From
          </Label>
          <Input
            id="sending-start"
            type="time"
            value={value.sending_start_time}
            disabled={disabled}
            onChange={(e) => set("sending_start_time", e.target.value)}
            className="h-9 w-[110px]"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="sending-end" className="text-xs font-medium text-slate-600">
            To
          </Label>
          <Input
            id="sending-end"
            type="time"
            value={value.sending_end_time}
            disabled={disabled}
            onChange={(e) => set("sending_end_time", e.target.value)}
            className="h-9 w-[110px]"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="daily-limit" className="text-xs font-medium text-slate-600">
            New contacts per day
          </Label>
          <Input
            id="daily-limit"
            type="number"
            min={1}
            max={1000}
            value={value.daily_limit}
            disabled={disabled}
            onChange={(e) => set("daily_limit", Number(e.target.value))}
            className="h-9"
          />
          <p className="text-[11px] leading-snug text-slate-400">
            How many new contacts Prism hands to Lemlist per day. Mailbox limits stay in Lemlist.
          </p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="default-delay" className="text-xs font-medium text-slate-600">
            Default follow-up delay (days)
          </Label>
          <Input
            id="default-delay"
            type="number"
            min={0}
            max={60}
            value={value.default_delay_days}
            disabled={disabled}
            onChange={(e) => set("default_delay_days", Number(e.target.value))}
            className="h-9"
          />
          <p className="text-[11px] leading-snug text-slate-400">Given to each new follow-up step.</p>
        </div>
      </div>
    </div>
  );
}
